import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  displayFinancialValue,
  FINANCIAL_GROUPS,
  parseDnbFinancialReport,
} from './utils/dnbFinancialReport';

const INK = '#17233D';
const NAVY = '#172C55';
const MUTED = '#64748B';
const BORDER = '#E3E8F1';
const SURFACE = '#F6F8FC';

const KpiCard = ({ label, value, accent }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      p: 2,
      borderRadius: 2.5,
      borderColor: BORDER,
      boxShadow: '0 6px 20px rgba(23, 44, 85, 0.04)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        bgcolor: accent,
      },
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 800, color: MUTED, letterSpacing: '.06em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.8, fontSize: { xs: 20, lg: 23 }, lineHeight: 1.15, fontWeight: 850, color: INK }}>
      {value}
    </Typography>
  </Card>
);

const MetricGroup = ({ group, statement }) => {
  const rows = group.labels
    .filter((label) => statement.metricsByLabel[label])
    .map((label) => ({ label, value: displayFinancialValue(statement.metricsByLabel[label], label) }));
  if (!rows.length) return null;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, borderColor: BORDER, overflow: 'hidden', height: '100%' }}>
      <Box sx={{ px: 2.25, py: 1.7, bgcolor: SURFACE, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontWeight: 850, color: INK, fontSize: 15 }}>{group.title}</Typography>
        <Typography sx={{ mt: 0.25, color: MUTED, fontSize: 11.5 }}>{group.subtitle}</Typography>
      </Box>
      <Box>
        {rows.map((row, index) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 2.25,
              py: 1.2,
              bgcolor: index % 2 ? '#FBFCFE' : '#fff',
              borderBottom: index < rows.length - 1 ? `1px solid ${BORDER}` : 0,
            }}
          >
            <Typography sx={{ color: '#46536A', fontSize: 13 }}>{row.label}</Typography>
            <Typography sx={{ color: INK, fontSize: 13, fontWeight: 750, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

function DnbFinancialReportDashboard({ markdownText, source }) {
  const report = useMemo(() => parseDnbFinancialReport(markdownText), [markdownText]);
  const [selectedPeriod, setSelectedPeriod] = useState(report.statements[0]?.periodEnd || '');

  useEffect(() => {
    setSelectedPeriod(report.statements[0]?.periodEnd || '');
  }, [report]);

  const statement = report.statements.find((item) => item.periodEnd === selectedPeriod) || report.statements[0];
  if (!statement) return null;

  const kpis = [
    ['Sales revenue', '#3276D2'],
    ['Gross profit', '#6A5AE0'],
    ['Net income', '#2F9D75'],
    ['Total assets', '#E39A35'],
    ['Total liabilities', '#D55B69'],
    ['Net worth', '#3C8E9F'],
  ];

  return (
    <Box data-testid="dnb-financial-dashboard">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.25, md: 3 },
          mb: 2,
          borderRadius: 3,
          color: '#fff',
          background: 'linear-gradient(120deg, #14284E 0%, #244A86 72%, #2E679D 100%)',
          overflow: 'hidden',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 250,
            height: 250,
            borderRadius: '50%',
            right: -90,
            top: -130,
            background: 'rgba(121, 189, 96, .14)',
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Box component="img" src="/logo.svg" alt="CrediArc" sx={{ width: 132, height: 'auto', mb: 2 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.11em', color: '#BFD2ED', textTransform: 'uppercase' }}>
              Financial intelligence
            </Typography>
            <Typography component="h3" sx={{ mt: 0.4, fontSize: { xs: 24, md: 30 }, fontWeight: 850, letterSpacing: '-.025em' }}>
              {report.companyName}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              <Chip icon={<CheckCircleOutlineIcon />} label="Verified by CrediArc" size="small" sx={{ bgcolor: 'rgba(255,255,255,.14)', color: '#fff', '& .MuiChip-icon': { color: '#8DD273' } }} />
              <Chip label={`${report.statements.length} statement periods`} size="small" sx={{ bgcolor: 'rgba(255,255,255,.1)', color: '#DDE8F6' }} />
            </Stack>
          </Box>
          <Stack spacing={1.2} sx={{ minWidth: { md: 245 }, alignSelf: { md: 'flex-end' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#DDE8F6' }}>
              <AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 12 }}>Financial data provided by CrediArc</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#DDE8F6' }}>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 12 }}>
                Retrieved {source?.fetchedAt ? new Date(source.fetchedAt).toLocaleString() : report.retrievedAt}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5, borderColor: BORDER }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: MUTED, letterSpacing: '.07em', textTransform: 'uppercase' }}>Selected statement</Typography>
            <Typography sx={{ mt: 0.4, color: INK, fontWeight: 800, fontSize: 16 }}>
              {statement.periodStart} to {statement.periodEnd}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
              <Chip size="small" label={statement.duration === '—' ? 'Duration unavailable' : statement.duration} variant="outlined" />
              <Chip size="small" label={statement.currency} variant="outlined" />
              {statement.reliability !== '—' && <Chip size="small" label={statement.reliability} variant="outlined" />}
            </Stack>
          </Box>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 250 } }}>
            <Select
              value={statement.periodEnd}
              onChange={(event) => setSelectedPeriod(event.target.value)}
              inputProps={{ 'aria-label': 'Financial statement period' }}
              sx={{ borderRadius: 2, fontWeight: 750, color: NAVY }}
            >
              {report.statements.map((item, index) => (
                <MenuItem key={`${item.periodStart}-${item.periodEnd}-${index}`} value={item.periodEnd}>
                  Ending {item.periodEnd} {item.duration !== '—' ? `• ${item.duration}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {kpis.map(([label, accent]) => (
          <Grid item xs={12} sm={6} lg={4} key={label}>
            <KpiCard
              label={label}
              accent={accent}
              value={displayFinancialValue(statement.metricsByLabel[label], label, { compact: true })}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {FINANCIAL_GROUPS.map((group) => (
          <Grid item xs={12} lg={6} key={group.title}>
            <MetricGroup group={group} statement={statement} />
          </Grid>
        ))}
      </Grid>

      <Typography sx={{ mt: 2, color: MUTED, fontSize: 11.5 }}>
        Values are shown exactly as provided by CrediArc. This report is deterministic and is not AI-generated.
      </Typography>
    </Box>
  );
}

export default DnbFinancialReportDashboard;
